import React from 'react';
import {ActivityIndicator, Image, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {BORDER_COLOR, HEADER_TITLE_COLOR, LOGGED_IN_COLOR, LOGGED_OUT_COLOR} from "../../Config/Colors";
import {WINDOW_HEIGHT, WINDOW_WIDTH} from "../../Config/Constants";
import MenuIcon from "../../Images/Icons/menu.png";
import LoginIcon from "../../Images/Icons/login.png";
import {useObserver} from "mobx-react-lite";
import {userRepository} from "../../Domain/Repositories/UserRepository";
import { FETCHING_SUCCESS_TITLE } from '../../Config/Translations';

interface ScreenTitleProps
{
    /**
     * Callback when the menu was pressed.
     */
    onMenuPressed?: () => void;

    /**
     * Screen name.
     */
    title?: string;

    /**
     * Toggle menu button.
     */
    showMenu?: boolean;

    /**
     * Underlines the bottom of the title.
     */
    underline?: boolean;

    /**
     * Toggles the loading indicator.
     */
    showLoading?: boolean;

    /**
     * The loading text.
     */
    loadingTitle?: string;

    /**
     * Callback when the login option has pressed.
     */
    onLoginPressed?: () => void;

    /**
     * Toggles the login button.
     */
    showLogin?: boolean;
}

/**
 * Renders the Screen Title.
 */
export default function ScreenTitle({
    onMenuPressed,
    title = "Power Clients Dashboard",
    showMenu = true,
    underline = false,
    showLoading = false,
    loadingTitle = FETCHING_SUCCESS_TITLE,
    onLoginPressed,
    showLogin = false,
}: ScreenTitleProps)
{
    const getUserTitle = (): string =>
    {
        return userRepository.token === '' ? "Login" : userRepository.user.emailAddress;
    }
    
    const getProfileColor = (): string =>
    {
        return userRepository.token === '' ? LOGGED_OUT_COLOR: LOGGED_IN_COLOR;
    }
    
    return useObserver(() => (
        <View style={[styles.container, {
            borderBottomColor: underline ? BORDER_COLOR : 'transparent',
            borderWidth: underline ? 2 : 0,
        }]}>

            {showLoading && (
                <View style={styles.indicatorView}>
                    <Text style={styles.indicatorText}>
                        {loadingTitle}
                    </Text> 
                        
                    <ActivityIndicator
                        size={WINDOW_HEIGHT * 0.025}
                        color={BORDER_COLOR}
                    />
                </View>
            )}

            {showLogin && (
                <TouchableOpacity
                    style={styles.loginView}
                    onPress={onLoginPressed}
                >
                    <Text
                        style={styles.loginNameText}
                        numberOfLines={1}
                    >
                        {getUserTitle()}
                    </Text>
                    
                    <View style={styles.loginButton}>
                        <Image
                            source={LoginIcon}
                            resizeMode={"contain"}
                            style={[styles.icon, {
                                tintColor: getProfileColor(),
                            }]}
                        />
                    </View>
                </TouchableOpacity>
            )}

            {showMenu && (
                <TouchableOpacity
                    style={styles.menuButton}
                    onPress={onMenuPressed}
                >
                    <Image
                        source={MenuIcon}
                        resizeMode={"contain"}
                        style={styles.icon}
                    />
                </TouchableOpacity>
            )}
            
            <Text style={styles.titleText}>
                {title}
            </Text>
        </View>
    ));
}

const styles = StyleSheet.create({
    container: {
        justifyContent: 'flex-start',
        width: '100%',
        flex: 0.07,
        flexDirection: 'row',
        alignItems: 'center',
        borderTopColor: 'transparent',
        borderLeftColor: 'transparent',
        borderRightColor: 'transparent',
        borderRightWidth: 0,
        borderLeftWidth: 0,
        borderTopWidth: 0,
    },
    indicatorView: {
        width: '30%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'flex-end',
        position: 'absolute',
        right: 0,
        flexDirection: 'row',
    },
    indicatorText: {
        color: HEADER_TITLE_COLOR,
        fontSize: WINDOW_WIDTH * 0.011,
        fontWeight: "600",
        textAlign: 'left',
        marginRight: '1.5%',
    },
    loadingIndicator: {
        position: 'absolute',
        right: 0,
        marginRight: '0.5%',
    },
    titleText: {
        color: HEADER_TITLE_COLOR,
        fontSize: WINDOW_WIDTH * 0.013,
        fontWeight: "600",
        paddingLeft: '1.3%',
        width: '100%',
        textAlign: 'left',
    },
    menuButton: {
        height: '100%',
        width: undefined,
        aspectRatio: 1,
        opacity: 0.7,
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: '0.65%',
    },
    loginView: {
        width: '20%',
        height: '100%',
        position: 'absolute',
        right: 0,
        justifyContent: 'center',
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: '0.5%',
    },
    loginButton: {
        height: '100%',
        width: undefined,
        aspectRatio: 1,
        opacity: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    loginNameText: {
        color: HEADER_TITLE_COLOR,
        fontSize: WINDOW_WIDTH * 0.013,
        fontWeight: "600",
        width: '100%',
        textAlign: 'right',
    },
    icon: {
        width: undefined,
        height: '70%',
        aspectRatio: 1,
    },
});
